'use client';

import { useQuery } from '@rocicorp/zero/react';
import { queries } from '../zero/queries';
import { useCallback, useEffect, useRef, useState } from 'react';
import { runZeroMutation } from '../zero/run-mutation';
import { useAppZero } from '../zero/use-app-zero';
import type { QueryClassification } from '../search/classify-query';
import type {
  RetrievalMode,
  SearchHit,
  SearchResponse,
  SearchResponseMode
} from '../search/types';

const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;
/** Hard ceiling so a hung request can never leave the UI stuck in 'loading'. */
const REQUEST_TIMEOUT_MS = 15_000;

export type KnowledgeSearchStatus =
  'default' | 'guidance' | 'loading' | 'ready' | 'empty' | 'error' | 'offline';

export type DefaultSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseKnowledgeSearchOptions {
  organizationId: string | undefined;
  /** Only fetches while the search surface is actually open. */
  open: boolean;
  currentUserId: string | undefined;
}

interface UseKnowledgeSearchResult {
  term: string;
  setTerm: (term: string) => void;
  submit: () => void;
  status: KnowledgeSearchStatus;
  mode: SearchResponseMode | null;
  effectiveQuery: string;
  results: SearchHit[];
  retrievalMode: RetrievalMode | null;
  hasMore: boolean;
  /** The classified intent behind `results` -- `null` before a response with an `intent` has arrived. */
  intent: QueryClassification['intent'] | null;
  /** A synthesized, cited answer for `intent: 'question'`, or `null` when none was generated. */
  answer: { text: string; citedHitIds: string[] } | null;
  /**
   * Best-effort guess, made client-side before the response arrives, that
   * the in-flight request is likely to synthesize an answer -- mirrors
   * `classify-query.ts`'s own cheap "contains a '?'" gate. Lets the UI show
   * a distinct "Finding an answer…" label while `status === 'loading'`
   * without threading the server's actual classification timing through the
   * hook.
   */
  isLikelyQuestion: boolean;
  retry: () => void;
  saveAsDefault: () => Promise<void>;
  resetDefault: () => Promise<void>;
  saveStatus: DefaultSaveStatus;
  defaultPending: boolean;
  savedQuery: string | null;
}

/**
 * Shared controller for the knowledge search surface (desktop dialog and
 * mobile drawer both use this): debounced typed search against
 * `POST /api/search`, default/Recommended fetch on open, request
 * abort/late-response rejection keyed on organization+query+request
 * identity, and the save/reset-default mutations. No React, HTTP, or
 * preference logic lives in `retrieveKnowledge` itself -- this hook is the
 * transport, matching the plan's "core stays UI-agnostic" boundary.
 */
export function useKnowledgeSearch({
  organizationId,
  open,
  currentUserId
}: UseKnowledgeSearchOptions): UseKnowledgeSearchResult {
  const z = useAppZero();
  const [term, setTermState] = useState('');
  const [response, setResponse] = useState<SearchResponse | null>(null);
  const [status, setStatus] = useState<KnowledgeSearchStatus>('default');
  const scope = `${organizationId}:${currentUserId}`;
  const [stateScope, setStateScope] = useState(scope);
  const [preference] = useQuery(
    open && organizationId && currentUserId
      ? queries.searchPreferences.current({ organizationId })
      : undefined
  );
  const [save, setSave] = useState<{
    scope: string;
    query: string;
    status: DefaultSaveStatus;
  } | null>(null);
  const mutationIdRef = useRef(0);
  const mutationPendingRef = useRef(false);
  const currentRef = useRef({ scope, open, term });
  currentRef.current = { scope, open, term: stateScope === scope ? term : '' };

  const requestIdRef = useRef(0);
  const debounceRef = useRef<number | undefined>(undefined);
  const abortRef = useRef<AbortController | undefined>(undefined);
  const lastFetchKeyRef = useRef<string | null>(null);

  const runSearch = useCallback(
    async (query: string) => {
      if (!organizationId) return;
      const requestId = ++requestIdRef.current;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        REQUEST_TIMEOUT_MS
      );
      setStatus('loading');
      try {
        const response = await fetch('/api/search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ organizationId, query }),
          signal: controller.signal
        });
        if (requestId !== requestIdRef.current) return; // superseded
        if (!response.ok) {
          if (response.status === 429) {
            setStatus('error');
            setResponse(null);
            return;
          }
          throw new Error(`Search failed with status ${response.status}`);
        }
        const body = (await response.json()) as SearchResponse;
        if (requestId !== requestIdRef.current) return; // superseded
        setResponse(body);
        setStatus(body.results.length ? 'ready' : 'empty');
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        if ((error as { name?: string })?.name === 'AbortError') {
          // A genuinely hung request (our own timeout fired) must still
          // surface as an error, not leave the UI stuck showing skeletons
          // forever; a supersede-triggered abort is caught by the
          // requestId check above and never reaches here.
          setStatus('error');
          setResponse(null);
          return;
        }
        if (!navigator.onLine) {
          setStatus('offline');
          return;
        }
        setStatus('error');
        setResponse(null);
      } finally {
        window.clearTimeout(timeoutId);
      }
    },
    [organizationId]
  );

  const visibleTerm = stateScope === scope ? term : '';
  const trimmed = visibleTerm.trim();

  // Clear synchronously on organization/close change so a previous
  // organization's or query's results never flash as current.
  useEffect(() => {
    setStateScope(scope);
    setTermState('');
    setResponse(null);
    setStatus('default');
    setSave(null);
    mutationIdRef.current++;
    mutationPendingRef.current = false;
    lastFetchKeyRef.current = null;
    requestIdRef.current++;
    abortRef.current?.abort();
    return () => {
      requestIdRef.current++;
      mutationIdRef.current++;
      window.clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, [scope, open]);

  useEffect(() => {
    // Clear any pending debounced search unconditionally, even when bailing
    // out below (dialog just closed, or no organization yet) -- otherwise a
    // scheduled search from before close still fires later and can resolve
    // after the surface reopens with a different query already showing.
    window.clearTimeout(debounceRef.current);
    if (!open || !organizationId || stateScope !== scope) return;

    if (trimmed.length === 0) {
      const key = `default:${organizationId}`;
      if (lastFetchKeyRef.current !== key) {
        lastFetchKeyRef.current = key;
        void runSearch('');
      }
      return;
    }
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setStatus('guidance');
      return;
    }
    const key = `typed:${organizationId}:${trimmed}`;
    if (lastFetchKeyRef.current === key) return;
    debounceRef.current = window.setTimeout(() => {
      lastFetchKeyRef.current = key;
      void runSearch(trimmed);
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(debounceRef.current);
  }, [trimmed, organizationId, open, runSearch, scope, stateScope]);

  const setTerm = useCallback((next: string) => {
    if (next === currentRef.current.term) return;
    const sameQuery = next.trim() === currentRef.current.term.trim();
    currentRef.current.term = next;
    setTermState(next);
    if (sameQuery) return;
    // Invalidate immediately, including the debounce window and one-letter queries.
    requestIdRef.current++;
    abortRef.current?.abort();
    window.clearTimeout(debounceRef.current);
    lastFetchKeyRef.current = null;
    setResponse(null);
    setStatus(next.trim().length === 1 ? 'guidance' : 'loading');
  }, []);

  const submit = useCallback(() => {
    if (trimmed.length < MIN_QUERY_LENGTH) return;
    window.clearTimeout(debounceRef.current);
    lastFetchKeyRef.current = `typed:${organizationId}:${trimmed}`;
    void runSearch(trimmed);
  }, [trimmed, organizationId, runSearch]);

  const retry = useCallback(() => {
    lastFetchKeyRef.current = null;
    void runSearch(trimmed);
  }, [runSearch, trimmed]);

  const updateDefault = useCallback(
    async (reset: boolean) => {
      if (
        !organizationId ||
        !currentUserId ||
        !open ||
        mutationPendingRef.current ||
        (!reset && trimmed.length < MIN_QUERY_LENGTH)
      )
        return;
      const mutationId = ++mutationIdRef.current;
      mutationPendingRef.current = true;
      setSave({ scope, query: trimmed, status: 'saving' });
      const ok = await runZeroMutation(
        () =>
          reset
            ? z.mutate.searchPreferences.resetDefault({
                organizationId,
                userId: currentUserId
              })
            : z.mutate.searchPreferences.setDefault({
                organizationId,
                defaultQuery: trimmed,
                userId: currentUserId
              }),
        { quiet: true }
      );
      if (mutationId !== mutationIdRef.current) return;
      mutationPendingRef.current = false;
      setSave({ scope, query: trimmed, status: ok ? 'saved' : 'error' });
      if (
        ok &&
        reset &&
        currentRef.current.open &&
        currentRef.current.scope === scope &&
        !currentRef.current.term.trim()
      ) {
        lastFetchKeyRef.current = `default:${organizationId}`;
        void runSearch('');
      }
    },
    [organizationId, currentUserId, open, trimmed, scope, z, runSearch]
  );

  const currentResponse = stateScope === scope && open ? response : null;
  const saveStatus =
    save?.scope === scope && save.query === trimmed ? save.status : 'idle';

  return {
    term: visibleTerm,
    setTerm,
    submit,
    status: stateScope === scope ? status : 'default',
    mode: currentResponse?.mode ?? null,
    effectiveQuery: currentResponse?.effectiveQuery ?? '',
    results: currentResponse?.results ?? [],
    retrievalMode: currentResponse?.retrievalMode ?? null,
    hasMore: currentResponse?.hasMore ?? false,
    intent: currentResponse?.intent ?? null,
    answer: currentResponse?.answer ?? null,
    isLikelyQuestion: trimmed.includes('?'),
    retry,
    saveAsDefault: () => updateDefault(false),
    resetDefault: () => updateDefault(true),
    savedQuery: preference?.defaultQuery ?? null,
    defaultPending: save?.scope === scope && save.status === 'saving',
    saveStatus
  };
}
